// src/app/dashboard-admin/dashboard-admin.component.ts
import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit, ChangeDetectorRef, HostListener } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { HttpService } from '../https.service';
import { URLUtils } from '../urlUtils';
import { Observable } from 'rxjs';

// ─────────────────────────────────────────────
//  Interfaces
// ─────────────────────────────────────────────
export interface ProfileCompletionRange {
  range: string;
  label: string;
  count: number;
  color: string;
  users: any[];
}

export interface SummaryMetric {
  icon: string;
  label: string;
  value: number | string;
  trend: number;
  trendLabel: string;
}

export interface OnboardingUserItem {
  id: string;
  firm_id: string;
  name: string;
  category: string;
}

// Updated OnboardingApiResponse for new flat API structure (no error/data wrapper) —
// mirrors EngagementTrendApiResponse's shape. Now requires start_date/end_date query params.
export interface OnboardingApiResponse {
  summary: {
    total_count: number;
    previous_total: number;
    change_percentage: number;
    trend: 'up' | 'down' | 'stable';
  };
  graph: {
    x_axis: {
      labels: string[];
    };
    y_axis: {
      min: number;
      max: number;
      step: number;
      unit?: string;
      label?: string;
    };
    datasets: {
      label: string;
      value: number;
      details?: {
        count: number;
        items: OnboardingUserItem[];
      };
    }[];
  };
}

// The /login/trend (24h summary) endpoint actually returns a simple
// error/data wrapper with a flat count/comparison/users list — it does NOT
// share DownloadTrendApiResponse's android/ios graph+datasets shape. (It was
// previously assumed to mirror DownloadTrendApiResponse, which caused the
// "Total Logged-in Users (24h)" card to always read 0.)
export interface LoginTrendApiResponse {
  error: boolean;
  data: {
    title: string;
    count: number;
    comparison: {
      previous_count: number;
      change_percentage: number;
      trend: 'up' | 'down' | 'stable';
    };
    users: {
      user_id: string;
      // Mongo _id of the user's firm — distinct from firm_uid (the short
      // public firm code). The lauditor search drill-down matches on this
      // pair (user_id + firm_id), so both must be carried through.
      firm_id: string;
      user_name: string;
      firm_uid: string;
      firm_name: string;
      last_login: string;
      category: string;
    }[];
  };
}

export interface DownloadTrendDevice {
  user_id: string | null;
  user_name?: string;
  installed_at: string;
  device_name: string;
}

export interface MobileTrendPlatform {
  platform: 'android' | 'ios';
  count: number;
  devices: DownloadTrendDevice[];
}

// Updated DownloadTrendApiResponse for new flat API structure (no error/data wrapper) —
// requires start_date/end_date query params, and per-bucket android/ios breakdown now
// lives under each dataset's details.platforms[] instead of android_count/ios_count.
export interface DownloadTrendApiResponse {
  summary: {
    android: {
      total_count: number;
      previous_total: number;
      change_percentage: number;
      trend: 'up' | 'down' | 'stable';
    };
    ios: {
      total_count: number;
      previous_total: number;
      change_percentage: number;
      trend: 'up' | 'down' | 'stable';
    };
  };
  graph: {
    x_axis: {
      labels: string[];
    };
    y_axis: {
      min: number;
      max: number;
      step: number;
      unit?: string;
      label?: string;
    };
    datasets: {
      label: string;
      value: number;
      details?: {
        platforms: MobileTrendPlatform[];
      };
    }[];
  };
}


export interface EngagementTrendApiResponse {
  summary: {
    total_count: number;
    previous_total: number;
    change_percentage: number;
    trend: 'up' | 'down' | 'stable';
  };
  graph: {
    x_axis: {
      labels: string[];
    };
    y_axis: {
      min: number;
      max: number;
      step: number;
      unit?: string;
      label?: string;
    };
    datasets: {
      label: string;
      value: number;
      details: {
        segments: {
          label: string;   // '<30' | '30-90' | '>90'
          count: number;
          user_ids?: string[];
        }[];
      };
    }[];
  };
}

// ─────────────────────────────────────────────
//  Location Distribution Interfaces
// ─────────────────────────────────────────────
export interface LocationCityItem {
  city: string;
  count: number;
}

export interface LocationDistributionApiResponse {
  error: boolean;
  data: {
    summary: {
      total_cities: number;
      total_lawyers: number;
      top_cities: {
        city: string;
        count: number;
      };
    };
    cities: LocationCityItem[];
  };
}

export interface CityListApiResponse {
  error: boolean;
  data: { city: string }[];
}

// ─────────────────────────────────────────────
//  Practice Areas / Services Distribution Interfaces
// ─────────────────────────────────────────────
export interface PracticeDistributionItem {
  name: string;
  count: number;
}

export interface PracticeDistributionApiResponse {
  error: boolean;
  data: {
    summary: {
      city: string;
      total_lawyers: number;
      top_practice_area?: { name: string; count: number };
      top_service?: { name: string; count: number };
    };
    practice_areas: PracticeDistributionItem[];
    services_offered: PracticeDistributionItem[];
  };
}

// Minimal Chart.js type shim
declare const Chart: any;

@Component({
  selector: 'app-dashboard-admin',
  templateUrl: './dashboard-admin.component.html',
  styleUrls: ['./dashboard-admin.component.css']
})
export class DashboardAdminComponent implements OnInit, OnDestroy, AfterViewInit {

  // ─── Loading ──────────────────────────────────────────────
  isLoadingProfile: boolean = false;
  isLoadingOnboarding: boolean = false;
  isLoadingEngagement: boolean = false;

  // ─── Date Range ───────────────────────────────────────────
  selectedDateRange: string = 'last30';

  // ─── Onboarding Card ──────────────────────────────────────
  // Filter dropdown replaces the old Daily/Weekly/Monthly/Quarterly segmented
  // buttons. 'daily' is kept as the underlying value/logic for the "Newly
  // Onboarded Users" summary card (see loadSummaryMetrics), but the Onboarding
  // Trend card itself now uses 'today' | 'yesterday' | 'last7'.
  onboardingFilter: string = 'today';
  // Unified 4-option filter list (All Time / Today / Yesterday / Last 7 Days)
  // shared by every analytics card's dropdown, so options/behavior stay in
  // sync across the dashboard. 'Custom' is deliberately NOT included here —
  // it only ever appears as a dynamically-added, display-only entry (see
  // getPeriodMenuOptions/isPeriodCustom) once the person has navigated away
  // from the selected option's default period; it is never a permanently
  // selectable dropdown entry.
  // Quarterly is intentionally left out of this list (hidden, not deleted) —
  // its date-range/label logic still exists in getOnboardingDateRangeParams
  // and computePeriodLabel for when it's re-enabled later.
  readonly PERIOD_FILTER_OPTIONS: { value: string; label: string }[] = [
    { value: 'all',       label: 'All Time' },
    { value: 'today',     label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'last7',     label: 'Last 7 Days' },
  ];
  // Same 3 options (no "All Time") shared by the Onboarding Trend, Engagement
  // Trend, and Mobile App Downloads cards' dropdowns — those cards always
  // operate on a bounded period, so "All Time" is intentionally excluded here
  // (unlike PERIOD_FILTER_OPTIONS, used by Location/Practice). "Custom" is
  // deliberately NOT included in this static list either — it is never a
  // directly selectable option; it only ever appears as a dynamically-added,
  // display-only entry once ◀/▶ navigation has moved the period away from
  // the selected option's default (see getPeriodMenuOptions/isPeriodCustom).
  readonly TREND_FILTER_OPTIONS: { value: string; label: string }[] = [
    { value: 'today',     label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'last7',     label: 'Last 7 Days' },
  ];
  onboardingFilterOptions: { value: string; label: string }[] = this.TREND_FILTER_OPTIONS;
  // Open/closed state for the custom onboarding filter dropdown (replaces
  // the native <select> so the options panel can be styled properly).
  onboardingFilterMenuOpen: boolean = false;

  // Same option list as onboardingFilterOptions — kept as its own bound
  // property (rather than every template referencing PERIOD_FILTER_OPTIONS
  // directly) so the Lawyer Distribution by Location / Practice Areas &
  // Services Distribution dropdowns can keep their existing template
  // bindings untouched.
  locationPracticeFilterOptions: { value: string; label: string }[] = this.PERIOD_FILTER_OPTIONS;
  // How many 7-day windows back from the current/live window we're viewing.
  // 0 = current/live window (only relevant when onboardingFilter === 'last7').
  onboardingWeekOffset: number = 0;
  // How many calendar days back from the selected anchor (Today = day 0,
  // Yesterday = day 0 relative to yesterday) we're viewing. 0 = the anchor
  // day itself (only relevant when onboardingFilter is 'today' or
  // 'yesterday'). Navigating past 0 surfaces the "Custom" label.
  onboardingDayOffset: number = 0;
  onboardingTotal: number = 0;
  onboardingTrend: number = 0;
  onboardingPeriodLabel: string = '';
  onboardingChartInstance: any = null;

  // ─── Profile Completion Card ──────────────────────────────
  profileTotal: number = 0;
  profileRanges: ProfileCompletionRange[] = [];
  donutChartInstance: any = null;
  hoveredSegment: ProfileCompletionRange | null = null;
  tooltipX: number = 0;
  tooltipY: number = 0;
  activeSegmentIndex: number | null = null;

  onboardingDateRange: string = '';

  // Store the exact start_date/end_date sent to /onboard/trend for the currently
  // loaded chart data, so drill-down navigation to lexiz-lawyers can reuse them
  // (same pattern as the Location Distribution card).
  private onboardingCurrentRange: { start_date: string; end_date: string } = { start_date: '', end_date: '' };
  // Same, but for the "Newly Onboarded Users" summary card (always the daily/24h window).
  private summaryOnboardingRange: { start_date: string; end_date: string } = { start_date: '', end_date: '' };

  // Builds a human-readable period label from the start/end dates actually sent to the API
  // (the new flat API response no longer echoes back a `filter`/per-bucket date fields).
  private setOnboardingDateRange(filter: string, start_date: string, end_date: string, dayOffset: number = 0): void {
    if (filter === 'daily') {
      const today = new Date();
      this.onboardingDateRange = 'Today, ' + today.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      return;
    }

    if (filter === 'all') {
      this.onboardingDateRange = 'Entire Dataset';
      return;
    }

    const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const fmt = (d: Date) => `${monthNames[d.getMonth()]} ${d.getDate()}`;

    // "Today" / "Yesterday" show a single calendar date with the year
    // (e.g. "Jul 20, 2026"), computed from the client's local timezone —
    // not from parsing the UTC start/end strings, since those cross
    // midnight in UTC for most timezones. dayOffset shifts the anchor back
    // further when the person has navigated via ◀ (see onOnboardingWeekNav).
    if (filter === 'today') {
      const target = this.addDays(new Date(), -dayOffset);
      this.onboardingDateRange = `${fmt(target)}, ${target.getFullYear()}`;
      return;
    }
    if (filter === 'yesterday') {
      const y = this.addDays(new Date(), -(1 + dayOffset));
      this.onboardingDateRange = `${fmt(y)}, ${y.getFullYear()}`;
      return;
    }

    const start = start_date ? new Date(start_date) : null;
    const end = end_date ? new Date(end_date) : null;

    if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) {
      this.onboardingDateRange = '';
      return;
    }

    if (filter === 'quarterly' && start.getFullYear() !== end.getFullYear()) {
      this.onboardingDateRange = `${fmt(start)} ${start.getFullYear()} – ${fmt(end)} ${end.getFullYear()}`;
    } else if (filter === 'quarterly') {
      this.onboardingDateRange = `${fmt(start)} – ${fmt(end)} ${end.getFullYear()}`;
    } else if (filter === 'last7') {
      // "Last 7 Days" shows the local calendar-day range regardless of the
      // exact UTC instant end_date carries (which, for the live/current
      // window, is "now" rather than end-of-day).
      const { start: rangeStart, end: rangeEnd } = this.getOnboardingLast7LocalDays(this.onboardingWeekOffset);
      this.onboardingDateRange = `${fmt(rangeStart)} – ${fmt(rangeEnd)}`;
    } else {
      this.onboardingDateRange = `${fmt(start)} – ${fmt(end)}`;
    }
  }

  readonly RANGE_CONFIG: { range: string; label: string; color: string }[] = [
    { range: '0-25',   label: '0–25%',   color: '#3b82f6' },
    { range: '25-50',  label: '25–50%',  color: '#8b5cf6' },
    { range: '50-75',  label: '50–75%',  color: '#f59e0b' },
    { range: '75-90',  label: '75–90%',  color: '#10b981' },
    { range: '90-100', label: '90–100%', color: '#0ea5e9' },
  ];

  // ─── Onboarding users (full list from API, used for point-click navigation) ─
  private onboardingAllUsers: OnboardingUserItem[] = [];
  private onboardingDatasets: OnboardingApiResponse['graph']['datasets'] = [];

  // ─── Summary card user lists (for clicking the top summary cards) ─────────
  private summaryOnboardingUsers: OnboardingUserItem[] = [];
  private summaryMobileUsers: DownloadTrendDevice[] = [];   // from Card 3 daily download API
  private loginTrendUsers: {
    user_id: string | null;
    firm_id?: string | null;
    user_name?: string;
    firm_uid?: string;
    firm_name?: string;
    last_login?: string;
    category?: string;
  }[] = [];

  // Store the exact start_date/end_date sent to /login/trend for the currently
  // loaded "Total Logged-in Users (24h)" card, so drill-down navigation to
  // lexiz-lawyers can reuse them (same pattern as the Location Distribution card).
  private loginCurrentRange: { start_date: string; end_date: string } = { start_date: '', end_date: '' };

  // ─── Engagement Trend Card (Analytics Row) ────────────────
  // Filter dropdown uses the same unified All Time / Today / Yesterday /
  // Last 7 Days list as every other card (see PERIOD_FILTER_OPTIONS),
  // defaulting to 'today'.
  engagementFilter: string = 'yesterday';
  // Engagement Trend intentionally excludes 'Today' from its dropdown (product
  // request) — kept as its own list rather than reusing TREND_FILTER_OPTIONS so
  // hiding it here doesn't affect the Onboarding Trend / Mobile App Downloads
  // cards, which still offer 'Today'. Commented out rather than deleted, in
  // case it needs to be restored later.
  readonly ENGAGEMENT_FILTER_OPTIONS: { value: string; label: string }[] = [
    // { value: 'today', label: 'Today' },   // hidden — Engagement Trend excludes 'Today' by design
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'last7',     label: 'Last 7 Days' },
  ];
  engagementFilterOptions: { value: string; label: string }[] = this.ENGAGEMENT_FILTER_OPTIONS;
  engagementFilterMenuOpen: boolean = false;
  engagementWeekOffset: number = 0;
  // See onboardingDayOffset for semantics.
  engagementDayOffset: number = 0;
  engagementDateRange: string = '';
  engagementError: boolean = false;
  engagementTotal: number = 0;
  engagementPreviousTotal: number = 0;
  engagementTrend: number = 0;
  engagementVsLabel: string = 'vs Previous 24 Hours';
  engagementChartInstance: any = null;

  // Store raw API datasets for tooltip lookups; also drives empty-state check in template
  engagementDatasets: EngagementTrendApiResponse['graph']['datasets'] = [];

  // Store the exact start_date/end_date sent to /engagement/trend for the currently
  // loaded chart data, so drill-down navigation to lexiz-lawyers can reuse them
  // (same pattern as the Onboarding Trend card).
  private engagementCurrentRange: { start_date: string; end_date: string } = { start_date: '', end_date: '' };

  // ─── Mobile App Usage Card ────────────────────────────────
  // Filter dropdown mirrors the Onboarding Trend card's Today / Yesterday /
  // Last 7 Days pattern (see onboardingFilterOptions, reused here).
  mobileFilter: string = 'today';                // today | yesterday | last7
  mobileFilterOptions: { value: string; label: string }[] = this.TREND_FILTER_OPTIONS;
  mobileFilterMenuOpen: boolean = false;
  mobileWeekOffset: number = 0;
  // See onboardingDayOffset for semantics.
  mobileDayOffset: number = 0;
  mobileDateRange: string = '';
  isLoadingMobile: boolean = false;
  mobileAndroidTotal: number = 0;
  mobileIosTotal: number = 0;
  mobileAndroidTrend: number = 0;
  mobileIosTrend: number = 0;
  mobileChartInstance: any = null;

  // Store raw API datasets per filter for drill-down navigation
  private mobileApiDatasets: DownloadTrendApiResponse['graph']['datasets'] = [];

  // Store the exact start_date/end_date sent to /download/trend for the currently
  // loaded chart data, so drill-down navigation to lexiz-lawyers can reuse them
  // (same pattern as the Location Distribution card).
  private mobileCurrentRange: { start_date: string; end_date: string } = { start_date: '', end_date: '' };
  // Same, but for the "Mobile App Downloads (Last 24h)" summary card.
  private summaryMobileRange: { start_date: string; end_date: string } = { start_date: '', end_date: '' };

  // ─── Lawyer Distribution by Location Card ────────────────
  // Filter dropdown mirrors the Onboarding Trend card's Today / Yesterday /
  // Last 7 Days pattern (see onboardingFilterOptions, reused here).
  locationFilter: string = 'all';                // all | today | yesterday | last7
  locationFilterMenuOpen: boolean = false;
  locationWeekOffset: number = 0;
  // See onboardingDayOffset for semantics.
  locationDayOffset: number = 0;
  locationDateRange: string = '';
  isLoadingLocation: boolean = false;
  locationTotalCities: number = 0;
  locationTotalLawyers: number = 0;
  locationTopCity: string = '';
  locationTopCities: LocationCityItem[] = [];
  locationRemainingCities: number = 0;
  private locationMaxCount: number = 0;

  // ─── Practice Areas & Services Distribution Card ──────────
  // Filter dropdown mirrors the Onboarding Trend card's Today / Yesterday /
  // Last 7 Days pattern (see onboardingFilterOptions, reused here).
  practiceFilter: string = 'all';                // all | today | yesterday | last7
  practiceFilterMenuOpen: boolean = false;
  practiceWeekOffset: number = 0;
  // See onboardingDayOffset for semantics.
  practiceDayOffset: number = 0;
  practiceDateRange: string = '';
  practiceActiveTab: 'practice' | 'services' = 'practice';
  isLoadingPractice: boolean = false;
  practiceSelectedCity: string = '';
  practiceCityOptions: string[] = [];
  practiceSearchQuery: string = '';

  // Practice tab stats
  practiceTotalLawyers: number = 0;
  practiceTotalAreas: number = 0;
  practiceTopAreaName: string = '';
  practiceTopAreaCount: number = 0;
  practiceTopAreaPct: string = '0';

  // Services tab stats
  practiceTotalServices: number = 0;
  practiceTopServiceName: string = '';
  practiceTopServiceCount: number = 0;
  practiceTopServicePct: string = '0';

  // Full lists from API (unfiltered by search)
  private practiceAreasFull: PracticeDistributionItem[] = [];
  private servicesFull: PracticeDistributionItem[] = [];

  // Filtered list shown in table (reactive to search)
  practiceFilteredList: PracticeDistributionItem[] = [];
  private practiceMaxCount: number = 0;

  // ─── Summary Metrics ──────────────────────────────────────
  summaryMetrics: SummaryMetric[] = [
    { icon: 'bi-person-plus',   label: 'Newly Onboarded Users',           value: 0, trend: 0, trendLabel: 'vs yesterday'   },
    { icon: 'bi-person-check',  label: 'Total Logged-in Users (24h)',     value: 0, trend: 0, trendLabel: 'vs yesterday'   },
    { icon: 'bi-download',      label: 'Mobile App Downloads (Last 24h)', value: 0, trend: 0, trendLabel: 'vs yesterday' },
  ];

  // ─── Canvas refs ─────────────────────────────────────────
  @ViewChild('onboardingCanvas')  onboardingCanvas!:  ElementRef<HTMLCanvasElement>;
  @ViewChild('donutCanvas')       donutCanvas!:       ElementRef<HTMLCanvasElement>;
  @ViewChild('engagementCanvas')  engagementCanvas!:  ElementRef<HTMLCanvasElement>;
  @ViewChild('mobileCanvas')      mobileCanvas!:      ElementRef<HTMLCanvasElement>;

  private chartsReady: boolean = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private httpService: HttpService,
    private spinner: NgxSpinnerService,
    private cdr: ChangeDetectorRef
  ) {}

  // ══════════════════════════════════════════════════════════
  //  Lifecycle
  // ══════════════════════════════════════════════════════════

  ngOnInit(): void {
    this.loadProfileCompletionData();
    this.loadSummaryMetrics();
    this.loadLocationDistribution(this.locationFilter);
    this.loadCityList();
  }

  ngAfterViewInit(): void {
    this.chartsReady = true;
    // Build onboarding & engagement charts after view is ready
    setTimeout(() => {
      this.loadOnboardingData(this.onboardingFilter);
      this.loadEngagementData(this.engagementFilter);
      this.loadMobileAppData(this.mobileFilter);
    }, 100);
  }

  ngOnDestroy(): void {
    this.destroyChart(this.onboardingChartInstance);
    this.destroyChart(this.donutChartInstance);
    this.destroyChart(this.engagementChartInstance);
    this.destroyChart(this.mobileChartInstance);
  }

  private destroyChart(chart: any): void {
    if (chart) { try { chart.destroy(); } catch (_) {} }
  }

  // ══════════════════════════════════════════════════════════
  //  API Calls
  // ══════════════════════════════════════════════════════════

  loadProfileCompletionData(): void {
    this.isLoadingProfile = true;
    this.httpService.sendReGetRequest(URLUtils.getProfileCompletionData).subscribe(
      (response: any) => {
        console.group('%c[Dashboard] Profile Completion API Response', 'color:#8b5cf6;font-weight:bold');
        console.log('Raw response:', response);
        console.log('total_users (from API):', response?.total_users);
        console.log('distribution (from API):', response?.distribution);
        console.log('users keys present:', response?.users ? Object.keys(response.users) : 'none');
        console.groupEnd();

        if (response && response.total_users !== undefined) {
          this.profileTotal = response.total_users;
          this.profileRanges = this.RANGE_CONFIG.map(cfg => ({
            ...cfg,
            count: (response.distribution?.find((d: any) => d.range === cfg.range)?.count ?? 0),
            users: response.users?.[cfg.range] ?? []
          }));

          console.group('%c[Dashboard] Parsed profileRanges', 'color:#8b5cf6;font-weight:bold');
          this.profileRanges.forEach(r => {
            console.log(
              `Range "${r.range}" → distribution count: ${r.count} | users array length: ${r.users.length}`,
              r.count !== r.users.length ? '⚠️ MISMATCH' : '✅ match'
            );
          });
          console.log('profileTotal:', this.profileTotal, '| sum of counts:', this.profileRanges.reduce((s, r) => s + r.count, 0));
          console.groupEnd();
        } else {
          this.profileRanges = this.RANGE_CONFIG.map(cfg => ({ ...cfg, count: 0, users: [] }));
          console.warn('[Dashboard] Profile completion response missing total_users — using empty fallback');
        }
        this.isLoadingProfile = false;
        this.cdr.detectChanges();
        setTimeout(() => this.buildDonutChart(), 0);
      },
      (_err: any) => {
        // Graceful fallback with mock data for preview
        this.profileTotal = 66;
        this.profileRanges = [
          { range: '0-25',   label: '0–25%',   color: '#3b82f6', count: 8,  users: [] },
          { range: '25-50',  label: '25–50%',  color: '#8b5cf6', count: 22, users: [] },
          { range: '50-75',  label: '50–75%',  color: '#f59e0b', count: 20, users: [] },
          { range: '75-90',  label: '75–90%',  color: '#10b981', count: 12, users: [] },
          { range: '90-100', label: '90–100%', color: '#0ea5e9', count: 4,  users: [] },
        ];
        this.isLoadingProfile = false;
        this.cdr.detectChanges();
        setTimeout(() => this.buildDonutChart(), 0);
      }
    );
  }

  loadSummaryMetrics(): void {
    // ── Card 1: Newly Onboarded Users — /onboard/trend (daily/24h window) ──
    const summaryRange = this.getOnboardingDateRangeParams('daily');
    this.summaryOnboardingRange = summaryRange;
    (this.httpService.sendReGetRequest(
      `${URLUtils.getOnboardingTrend}?start_date=${encodeURIComponent(summaryRange.start_date)}&end_date=${encodeURIComponent(summaryRange.end_date)}`
    ) as unknown as Observable<OnboardingApiResponse>).subscribe(
      (res: OnboardingApiResponse) => {
        if (res && res.summary) {
          const s = res.summary;
          const trend = s.trend === 'up'
            ? Math.abs(s.change_percentage)
            : -Math.abs(s.change_percentage);
          this.summaryMetrics[0] = {
            icon: 'bi-person-plus',
            label: 'Newly Onboarded Users',
            value: s.total_count,
            trend,
            trendLabel: 'vs yesterday',
          };
          // Store users for summary card click navigation —
          // new flat API nests users inside each dataset's details.items
          this.summaryOnboardingUsers = (res.graph?.datasets ?? [])
            .flatMap(d => d.details?.items ?? []);
        }
      },
      (_err: any) => { /* keep default zero value on error */ }
    );

    // ── Card 2: Total Logged-in Users (24h) — /login/trend (daily/24h window) ──
    const loginRange = this.getLoginDateRangeParams();
    this.loginCurrentRange = loginRange;
    (this.httpService.sendReGetRequest(
      `${URLUtils.getLoginTrend}?start_date=${encodeURIComponent(loginRange.start_date)}&end_date=${encodeURIComponent(loginRange.end_date)}`
    ) as unknown as Observable<LoginTrendApiResponse>).subscribe(
      (res: LoginTrendApiResponse) => {
        if (res && !res.error && res.data) {
          const { data } = res;
          const totalCount = data.count || 0;

          const overallTrend = data.comparison?.trend === 'up'
            ? Math.abs(data.comparison.change_percentage)
            : data.comparison?.trend === 'down'
              ? -Math.abs(data.comparison.change_percentage)
              : 0;

          this.summaryMetrics[1] = {
            icon: 'bi-person-check',
            label: 'Total Logged-in Users (24h)',
            value: totalCount,
            trend: overallTrend,
            trendLabel: 'vs yesterday',
          };
          // Store users for summary card click navigation — flat list straight
          // from the API, no platforms/devices nesting to flatten anymore.
          this.loginTrendUsers = data.users ?? [];
        }
      },
      (_err: any) => { /* keep default zero value on error */ }
    );

    // ── Card 3: Mobile App Downloads (Last 24h) — /download/trend (daily/24h window) ──
    const summaryMobileRange = this.getMobileDateRangeParams('last24h');
    this.summaryMobileRange = summaryMobileRange;
    (this.httpService.sendReGetRequest(
      `${URLUtils.getDownloadTrend}?start_date=${encodeURIComponent(summaryMobileRange.start_date)}&end_date=${encodeURIComponent(summaryMobileRange.end_date)}`
    ) as unknown as Observable<DownloadTrendApiResponse>).subscribe(
      (res: DownloadTrendApiResponse) => {
        if (res && res.summary) {
          const androidSummary = res.summary.android;
          const iosSummary = res.summary.ios;
          const totalDownloads = (androidSummary?.total_count || 0) + (iosSummary?.total_count || 0);
          
          // Calculate overall trend as weighted average of android and ios trends
          const androidTrend = androidSummary?.trend === 'up' 
            ? Math.abs(androidSummary.change_percentage) 
            : androidSummary?.trend === 'down' 
              ? -Math.abs(androidSummary.change_percentage) 
              : 0;
          const iosTrend = iosSummary?.trend === 'up' 
            ? Math.abs(iosSummary.change_percentage) 
            : iosSummary?.trend === 'down' 
              ? -Math.abs(iosSummary.change_percentage) 
              : 0;
          
          // Weighted average based on total counts
          let overallTrend = 0;
          if (totalDownloads > 0) {
            const androidWeight = (androidSummary?.total_count || 0) / totalDownloads;
            const iosWeight = (iosSummary?.total_count || 0) / totalDownloads;
            overallTrend = (androidTrend * androidWeight) + (iosTrend * iosWeight);
          } else {
            // If no downloads, check if either platform had previous downloads
            overallTrend = (androidSummary?.previous_total || 0) > 0 || (iosSummary?.previous_total || 0) > 0 ? -100 : 0;
          }

          this.summaryMetrics[2] = {
            icon: 'bi-download',
            label: 'Mobile App Downloads (Last 24h)',
            value: totalDownloads,
            trend: overallTrend,
            trendLabel: 'vs yesterday',
          };
          
          // Store all devices for summary card click navigation
          this.summaryMobileUsers = this.flattenMobileDevices(res.graph?.datasets ?? []);
        }
      },
      (_err: any) => { /* keep default zero value on error */ }
    );
  }

  // ══════════════════════════════════════════════════════════
  //  Chart — Onboarding Trend (Line/Area)
  // ══════════════════════════════════════════════════════════

  loadOnboardingData(filter: string, weekOffset: number = 0, dayOffset: number = 0): void {
    this.isLoadingOnboarding = true;
    this.destroyChart(this.onboardingChartInstance);
    this.onboardingChartInstance = null;

    const { start_date, end_date } = this.getOnboardingDateRangeParams(filter, weekOffset, dayOffset);
    this.onboardingCurrentRange = { start_date, end_date };
    // "All Time" omits start_date/end_date entirely rather than sending them empty.
    const onboardingDateParams = (start_date && end_date)
      ? `?start_date=${encodeURIComponent(start_date)}&end_date=${encodeURIComponent(end_date)}`
      : '';

    (this.httpService.sendReGetRequest(
      `${URLUtils.getOnboardingTrend}${onboardingDateParams}`
    ) as unknown as Observable<OnboardingApiResponse>).subscribe(
      (response: OnboardingApiResponse) => {
        if (response && response.summary && response.graph) {
          const { summary, graph } = response;

          // ── Summary metrics ──────────────────────────────
          this.onboardingTotal = summary.total_count;
          this.onboardingTrend = summary.trend === 'up'
            ? Math.abs(summary.change_percentage)
            : -Math.abs(summary.change_percentage);

          // ── Period label shown in the card header ─────────
          this.onboardingPeriodLabel = this.computePeriodLabel(filter, '');

          // ── Date range shown in the card metrics row ──────
          this.setOnboardingDateRange(filter, start_date, end_date, dayOffset);

          // ── Store datasets for point-click navigation ──
          this.onboardingDatasets = graph.datasets;
          // Flatten all users across all datasets for summary usage
          this.onboardingAllUsers = graph.datasets.flatMap(d => d.details?.items ?? []);

          // ── Build label→value map from API datasets ──────
          const dataMap = new Map<string, number>(
            graph.datasets.map(d => [d.label, d.value])
          );

          // Fill all x-axis labels (missing ones default to 0)
          const labels = graph.x_axis.labels;
          const data   = labels.map(lbl => dataMap.get(lbl) ?? 0);

          this.isLoadingOnboarding = false;
          this.cdr.detectChanges();
          this.buildOnboardingChart(labels, data, graph.y_axis.max, graph.y_axis.step, graph.datasets);
        } else {
          this.onboardingTotal = 0;
          this.onboardingTrend = 0;
          this.onboardingPeriodLabel = '';
          this.onboardingDateRange = '';
          this.isLoadingOnboarding = false;
          this.cdr.detectChanges();
        }
      },
      (_err: any) => {
        this.onboardingTotal = 0;
        this.onboardingTrend = 0;
        this.onboardingPeriodLabel = '';
        this.onboardingDateRange = '';
        this.isLoadingOnboarding = false;
        this.cdr.detectChanges();
      }
    );
  }

  buildOnboardingChart(
    labels: string[],
    data: number[],
    yMax?: number,
    yStep?: number,
    datasets?: OnboardingApiResponse['graph']['datasets']
  ): void {
    if (!this.onboardingCanvas) { return; }
    this.destroyChart(this.onboardingChartInstance);

    const ctx = this.onboardingCanvas.nativeElement.getContext('2d');
    if (!ctx) { return; }

    const gradient = ctx.createLinearGradient(0, 0, 0, 220);
    gradient.addColorStop(0, 'rgba(7,136,242,0.18)');
    gradient.addColorStop(1, 'rgba(7,136,242,0)');

    this.onboardingChartInstance = new Chart(ctx, {
      type: 'line',
      data: {
        labels,
        datasets: [{
          label: 'Onboarded',
          data,
          borderColor: '#0788f2',
          backgroundColor: gradient,
          fill: true,
          tension: 0.4,
          pointRadius: 4,
          pointHoverRadius: 7,
          pointBackgroundColor: '#fff',
          pointBorderColor: '#0788f2',
          pointBorderWidth: 2,
          borderWidth: 2.5,
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: '#fff',
            borderColor: '#e0e6ed',
            borderWidth: 1,
            titleColor: '#374151',
            bodyColor: '#374151',
            padding: 10,
            callbacks: {
              title: (items: any[]) => items.length ? items[0].label : '',
              label: (ctx: any) => `  Onboarded: ${ctx.parsed.y}`
            }
          }
        },
        onClick: (_evt: any, elements: any[]) => {
          if (elements.length > 0) {
            const idx   = elements[0].index;
            const label = labels[idx];
            this.navigateToOnboardingUsers(label);
          }
        },
        onHover: (_evt: any, elements: any[]) => {
          const canvas = this.onboardingCanvas?.nativeElement;
          if (canvas) {
            canvas.style.cursor = elements.length > 0 ? 'pointer' : 'default';
          }
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { font: { size: 11 }, color: '#9ca3af', maxRotation: 0 }
          },
          y: {
            beginAtZero: true,
            ...(yMax !== undefined ? { max: yMax } : {}),
            grid: { color: 'rgba(0,0,0,0.04)' },
            ticks: {
              font: { size: 11 },
              color: '#9ca3af',
              stepSize: yStep ?? 1
            },
            border: { display: false }
          }
        }
      }
    });
  }

  // ── Shared "Custom" period-dropdown helpers ─────────────────────────────
  // Reused by every filter dropdown (Onboarding, Engagement, Mobile,
  // Location, Practice) so the "navigate away → show Custom" behavior and
  // its display rules stay identical and in one place instead of being
  // reimplemented per card.

  // Single shared "Custom" entry + one cached options array per base list, so
  // getPeriodMenuOptions can hand back stable references (see the note there).
  private readonly CUSTOM_PERIOD_OPTION: { value: string; label: string } = { value: 'custom', label: 'Custom' };
  private readonly periodMenuOptionsWithCustom =
    new WeakMap<{ value: string; label: string }[], { value: string; label: string }[]>();

  /**
   * True once ◀ navigation has moved the currently-selected 'today' /
   * 'yesterday' / 'last7' filter away from its default (live) period —
   * i.e. the data on screen is a "Custom" day/week, not the selected
   * option's own period. 'all' never has a custom state.
   */
  isPeriodCustom(filter: string, weekOffset: number, dayOffset: number): boolean {
    if (filter === 'today' || filter === 'yesterday') { return dayOffset > 0; }
    if (filter === 'last7') { return weekOffset > 0; }
    return false;
  }

  /**
   * The dropdown's option list for the current render. "Custom" is never
   * part of the static options list (see TREND_FILTER_OPTIONS /
   * PERIOD_FILTER_OPTIONS) — it's appended here, only while isCustom is
   * true, so it is invisible until the person navigates into a custom
   * period and disappears again the moment they navigate back out.
   */
  getPeriodMenuOptions(baseOptions: { value: string; label: string }[], isCustom: boolean): { value: string; label: string }[] {
    if (!isCustom) { return baseOptions; }
    // The "with Custom" list is cached per base list (and the Custom entry is a
    // single shared object) so this returns the *same* array instance on every
    // change-detection pass. Building a fresh array/object here instead made
    // NgForOf's differ report changes on every pass while a menu was open,
    // which re-dirtied the view and spun change detection forever — the whole
    // page (not just the dropdown) locked up as soon as a card in its Custom
    // period had its dropdown opened.
    let withCustom = this.periodMenuOptionsWithCustom.get(baseOptions);
    if (!withCustom) {
      withCustom = [...baseOptions, this.CUSTOM_PERIOD_OPTION];
      this.periodMenuOptionsWithCustom.set(baseOptions, withCustom);
    }
    return withCustom;
  }

  /** trackBy for every filter dropdown's option list — keeps the menu's DOM
   *  stable across change-detection passes instead of tearing it down and
   *  rebuilding it whenever the option list is re-evaluated. */
  trackPeriodOption(_index: number, opt: { value: string; label: string }): string {
    return opt.value;
  }

  /**
   * Whether a given dropdown option should render as the active/checked
   * item. While a custom period is showing, only the synthetic "custom"
   * entry is active — the underlying filter value (e.g. 'today') must NOT
   * stay highlighted, since the data on screen no longer matches it.
   */
  isFilterOptionActive(optionValue: string, currentFilter: string, isCustom: boolean): boolean {
    return isCustom ? optionValue === 'custom' : optionValue === currentFilter;
  }

  onOnboardingFilterChange(filter: string): void {
    this.onboardingFilter = filter;
    this.onboardingWeekOffset = 0;
    this.onboardingDayOffset = 0;
    this.loadOnboardingData(filter, this.onboardingWeekOffset, this.onboardingDayOffset);
  }

  /** Toggles the custom onboarding filter dropdown open/closed. */
  toggleOnboardingFilterMenu(): void {
    this.onboardingFilterMenuOpen = !this.onboardingFilterMenuOpen;
  }

  /** Applies the chosen filter option and closes the dropdown. "custom" is
   *  a display-only entry (see getPeriodMenuOptions) — it is never a real
   *  selectable target, so picking it just closes the menu without
   *  changing the underlying filter/offsets. */
  selectOnboardingFilter(filter: string): void {
    this.onboardingFilterMenuOpen = false;
    if (filter === 'custom') { return; }
    this.onOnboardingFilterChange(filter);
  }

  /** Whether the Onboarding Trend card's Period row is currently showing a custom (navigated-away) period. */
  onboardingIsCustom(): boolean {
    return this.isPeriodCustom(this.onboardingFilter, this.onboardingWeekOffset, this.onboardingDayOffset);
  }

  /**
   * Label shown on the dropdown trigger for the currently selected filter.
   * Once the person navigates away from the selected option's default
   * period (via ◀), the trigger surfaces "Custom" instead — this label is
   * display-only and is never added to onboardingFilterOptions itself
   * (see TREND_FILTER_OPTIONS comment).
   */
  getOnboardingFilterLabel(): string {
    if (this.onboardingIsCustom()) { return 'Custom'; }
    const match = this.onboardingFilterOptions.find(opt => opt.value === this.onboardingFilter);
    return match ? match.label : '';
  }

  /** Closes the onboarding filter dropdown when clicking anywhere outside it. */
  @HostListener('document:click')
  onDocumentClickCloseOnboardingFilterMenu(): void {
    if (this.onboardingFilterMenuOpen) {
      this.onboardingFilterMenuOpen = false;
    }
  }

  /**
   * Closes the Mobile Downloads / Lawyer Distribution / Practice Areas /
   * Engagement Trend dropdowns when clicking anywhere outside them (same
   * pattern as onDocumentClickCloseOnboardingFilterMenu, kept as its own
   * listener so the Onboarding Trend card's implementation stays untouched).
   */
  @HostListener('document:click')
  onDocumentClickClosePeriodFilterMenus(): void {
    if (this.mobileFilterMenuOpen)     { this.mobileFilterMenuOpen = false; }
    if (this.locationFilterMenuOpen)   { this.locationFilterMenuOpen = false; }
    if (this.practiceFilterMenuOpen)   { this.practiceFilterMenuOpen = false; }
    if (this.engagementFilterMenuOpen) { this.engagementFilterMenuOpen = false; }
  }

  /**
   * Prev/Next arrows for the Onboarding Trend card's Period row. Applies to
   * 'today' / 'yesterday' (navigates onboardingDayOffset) and 'last7'
   * (navigates onboardingWeekOffset) — 'all' has no navigation. 'prev'
   * always moves further back in time (increments the relevant offset);
   * 'next' moves toward the anchor period (decrements it) and is a no-op
   * once that anchor is reached — navigation past the anchor (into the
   * future, or across Today/Yesterday's boundary) is not allowed.
   */
  onOnboardingWeekNav(direction: 'prev' | 'next'): void {
    if (this.onboardingFilter === 'last7') {
      if (direction === 'prev') {
        this.onboardingWeekOffset += 1;
      } else {
        if (this.onboardingWeekOffset === 0) { return; }
        this.onboardingWeekOffset -= 1;
      }
      this.loadOnboardingData(this.onboardingFilter, this.onboardingWeekOffset, this.onboardingDayOffset);
      return;
    }

    if (this.onboardingFilter === 'today' || this.onboardingFilter === 'yesterday') {
      if (direction === 'prev') {
        this.onboardingDayOffset += 1;
      } else {
        if (this.onboardingDayOffset === 0) { return; }
        this.onboardingDayOffset -= 1;
      }
      this.loadOnboardingData(this.onboardingFilter, this.onboardingWeekOffset, this.onboardingDayOffset);
      return;
    }
    // 'all' — no navigation.
  }

  /** Whether the ▶ (next) arrow should be disabled for the Onboarding Trend card's Period row. */
  isOnboardingNextDisabled(): boolean {
    if (this.onboardingFilter === 'last7') { return this.onboardingWeekOffset === 0; }
    if (this.onboardingFilter === 'today' || this.onboardingFilter === 'yesterday') { return this.onboardingDayOffset === 0; }
    return true;
  }

  /**
   * Shared "Period" row label for the Mobile Downloads / Lawyer Distribution /
   * Practice Areas / Engagement Trend cards' Today | Yesterday | Last 7 Days
   * dropdown. Mirrors the Onboarding Trend card's date-label formatting
   * (see setOnboardingDateRange / computePeriodLabel) but is kept separate so
   * the Onboarding Trend card's own implementation is never touched, and so
   * this can be reused as-is by every other card that adopts the dropdown.
   * All calendar-day boundaries are computed in the client's local timezone.
   */
  private computePeriodRangeLabel(filter: string, weekOffset: number = 0, dayOffset: number = 0): string {
    const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const fmt = (d: Date) => `${monthNames[d.getMonth()]} ${d.getDate()}`;

    if (filter === 'all') {
      return 'Entire Dataset';
    }
    if (filter === 'today') {
      const target = this.addDays(new Date(), -dayOffset);
      return `${fmt(target)}, ${target.getFullYear()}`;
    }
    if (filter === 'yesterday') {
      const y = this.addDays(new Date(), -(1 + dayOffset));
      return `${fmt(y)}, ${y.getFullYear()}`;
    }
    if (filter === 'last7') {
      // Reuses the same local-calendar-day window logic as the Onboarding
      // Trend card's Last 7 Days filter, so both stay perfectly in sync.
      const { start, end } = this.getOnboardingLast7LocalDays(weekOffset);
      return `${fmt(start)} – ${fmt(end)}`;
    }
    return '';
  }

  private computePeriodLabel(filter: string, apiPeriodLabel: string): string {
    const now = new Date();
    const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

    if (filter === 'daily') {
      return `Today, ${monthNames[now.getMonth()]} ${now.getDate()}`;
    }

    if (filter === 'today') {
      return `${monthNames[now.getMonth()]} ${now.getDate()}, ${now.getFullYear()}`;
    }

    if (filter === 'yesterday') {
      const y = this.addDays(now, -1);
      return `${monthNames[y.getMonth()]} ${y.getDate()}, ${y.getFullYear()}`;
    }

    if (filter === 'last7') {
      const { start, end } = this.getOnboardingLast7LocalDays(this.onboardingWeekOffset);
      const fmt = (d: Date) => `${monthNames[d.getMonth()]} ${d.getDate()}`;
      return `${fmt(start)} – ${fmt(end)}`;
    }

    if (filter === 'weekly') {
      if (apiPeriodLabel && /\w+ \d+ ?[-–] ?\w+ \d+/.test(apiPeriodLabel)) {
        return apiPeriodLabel.replace(' - ', ' – ');
      }
      const day  = now.getDay();
      const mon  = new Date(now); mon.setDate(now.getDate() - ((day + 6) % 7));
      const sun  = new Date(mon); sun.setDate(mon.getDate() + 6);
      const fmt  = (d: Date) => `${monthNames[d.getMonth()]} ${d.getDate()}`;
      return `${fmt(mon)} – ${fmt(sun)}`;
    }

    if (filter === 'monthly') {
      return `${monthNames[now.getMonth()]} ${now.getFullYear()}`;
    }

    if (filter === 'quarterly') {
      const q = Math.floor(now.getMonth() / 3) + 1;
      return `Q${q} ${now.getFullYear()}`;
    }

    return apiPeriodLabel ?? '';
  }

  // ══════════════════════════════════════════════════════════
  //  Chart — Profile Completion Donut
  // ══════════════════════════════════════════════════════════

  buildDonutChart(): void {
    if (!this.donutCanvas) { return; }
    this.destroyChart(this.donutChartInstance);

    const labels = this.profileRanges.map(r => r.label);
    const data   = this.profileRanges.map(r => r.count);
    const colors = this.profileRanges.map(r => r.color);

    const ctx = this.donutCanvas.nativeElement.getContext('2d');
    if (!ctx) { return; }

    this.donutChartInstance = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels,
        datasets: [{
          data,
          backgroundColor: colors,
          hoverBackgroundColor: colors.map(c => c + 'dd'),
          borderWidth: 2,
          borderColor: '#fff',
          hoverOffset: 10,
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '68%',
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: '#fff',
            borderColor: '#e0e6ed',
            borderWidth: 1,
            titleColor: '#374151',
            bodyColor: '#374151',
            padding: 10,
            callbacks: {
              label: (ctx: any) => {
                const pct = this.profileTotal > 0
                  ? ((ctx.parsed / this.profileTotal) * 100).toFixed(1)
                  : 0;
                return `  ${ctx.parsed} users (${pct}%)`;
              }
            }
          }
        },
        onClick: (_evt: any, elements: any[]) => {
          if (elements.length > 0) {
            const idx = elements[0].index;
            this.navigateToLawyers(this.profileRanges[idx]);
          }
        },
        onHover: (_evt: any, elements: any[]) => {
          const canvas = this.donutCanvas?.nativeElement;
          if (canvas) {
            canvas.style.cursor = elements.length > 0 ? 'pointer' : 'default';
          }
          this.activeSegmentIndex = elements.length > 0 ? elements[0].index : null;
        }
      }
    });
  }

  navigateToLawyers(range: ProfileCompletionRange): void {
    const ids = (range.users ?? [])
      .map((u: any) => u.firm_uid ?? '')
      .filter(Boolean);

    console.group('%c[Dashboard] navigateToLawyers clicked', 'color:#0788f2;font-weight:bold');
    console.log('Range clicked:', range.range, '|', range.label);
    console.log('range.count (from distribution):', range.count);
    console.log('range.users.length (users array):', range.users?.length ?? 0);
    console.log('firm_uid IDs extracted:', ids.length, ids);
    if (range.count !== (range.users?.length ?? 0)) {
      console.warn('⚠️ count vs users.length MISMATCH — distribution count may differ from users provided by API');
    }
    console.groupEnd();

    const queryParams: any = {
      profileCompletion: range.range,
      completionLabel:   range.label,
      account_type:      'client',
    };

    if (ids.length > 0) {
      queryParams['profileFilterIds'] = ids.join(',');
    }

    this.router.navigate(['/admin/lexiz-lawyers'], { queryParams });
  }

  navigateToSummaryOnboardingUsers(): void {
    const ids = this.summaryOnboardingUsers
      .map(u => u.firm_id ?? '')
      .filter(Boolean);
    if (!ids.length) { return; }
    const now = new Date();
    const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const todayLabel = `Today, ${monthNames[now.getMonth()]} ${now.getDate()}`;
    const { start_date, end_date } = this.summaryOnboardingRange;
    this.router.navigate(['/admin/lexiz-lawyers'], {
      queryParams: {
        onboardingIds:    ids.join(','),
        onboardingFilter: 'daily',
        onboardingPoint:  todayLabel,
        dateRange:        todayLabel,
        account_type:     'client',
        ...(start_date && end_date ? { startDatetime: start_date, endDatetime: end_date } : {})
      }
    });
  }

  navigateToLoginUsers(): void {
    const ids = [...new Set(
      this.loginTrendUsers.map(u => u.user_id).filter((id): id is string => !!id)
    )];
    if (!ids.length) { return; }
    // firm_id list travels alongside the user_id list so the lawyers list can
    // send the { user_ids, firm_ids } payload to /professional/search/lauditor.
    const firmIds = [...new Set(
      this.loginTrendUsers.map(u => u.firm_id).filter((id): id is string => !!id)
    )];
    // The id lists travel in router navigation state, NOT in the URL query
    // params — they exist only to be sent as the lauditor search request
    // payload, so they have no business in the address bar (same pattern as
    // profileFilterIds, read from history.state in LawyersListComponent).
    //
    // No startDatetime/endDatetime here either (unlike the onboarding / mobile
    // cards): the { user_ids, firm_ids } payload already pins the result set to
    // exactly the users behind this card, so /professional/search/lauditor must
    // not also be narrowed by a start_date/end_date window. 'Last 24 Hours'
    // stays as the display-only period label. loginCurrentRange is still
    // tracked for the card itself — it's just no longer forwarded.
    this.router.navigate(['/admin/lexiz-lawyers'], {
      queryParams: {
        dateRange: 'Last 24 Hours',
        account_type: 'client',
      },
      state: {
        loginUserIds: ids,
        loginFirmIds: firmIds,
      }
    });
  }

  navigateToSummaryMobileUsers(): void {
    const userIds = [...new Set(
      this.summaryMobileUsers.map(d => d.user_id).filter((id): id is string => !!id)
    )];
    const { start_date, end_date } = this.summaryMobileRange;
    const queryParams: any = {
      mobileFilter: 'last24h',
      dateRange:    'Last 24 Hours',
      account_type: 'client',
      ...(start_date && end_date ? { startDatetime: start_date, endDatetime: end_date } : {})
    };
    if (userIds.length > 0) {
      queryParams['mobileUserIds'] = userIds.join(',');
    }
    this.router.navigate(['/admin/lexiz-lawyers'], { queryParams });
  }

  navigateToOnboardingUsers(pointLabel: string): void {
    const dataset = this.onboardingDatasets.find(d => d.label === pointLabel);
    const value = dataset?.value ?? 0;
    if (value === 0) { return; }

    const ids = (dataset?.details?.items ?? []).map(u => u.firm_id ?? '').filter(Boolean);
    if (!ids.length) { return; }

    const { start_date, end_date } = this.onboardingCurrentRange;

    this.router.navigate(['/admin/lexiz-lawyers'], {
      queryParams: {
        onboardingIds:    ids.join(','),
        onboardingFilter: this.onboardingFilter,
        onboardingPoint:  pointLabel,
        dateRange:        this.onboardingDateRange || pointLabel,
        account_type:     'client',
        ...(start_date && end_date ? { startDatetime: start_date, endDatetime: end_date } : {})
      }
    });
  }

  getSegmentPercent(count: number): string {
    if (!this.profileTotal || this.profileTotal === 0) return '0';
    return ((count / this.profileTotal) * 100).toFixed(1);
  }

  // ══════════════════════════════════════════════════════════
  //  Engagement Trend Card — /engagement/trend
  // ══════════════════════════════════════════════════════════

  onEngagementFilterChange(filter: string): void {
    this.engagementFilter = filter;
    this.engagementWeekOffset = 0;
    this.engagementDayOffset = 0;
    this.loadEngagementData(filter, this.engagementWeekOffset, this.engagementDayOffset);
  }

  /** Toggles the Engagement Trend card's All Time/Today/Yesterday/Last 7 Days dropdown. */
  toggleEngagementFilterMenu(): void {
    this.engagementFilterMenuOpen = !this.engagementFilterMenuOpen;
  }

  /** Applies the chosen filter option and closes the dropdown (see selectOnboardingFilter for the "custom" no-op). */
  selectEngagementFilter(filter: string): void {
    this.engagementFilterMenuOpen = false;
    if (filter === 'custom') { return; }
    this.onEngagementFilterChange(filter);
  }

  /** Whether the Engagement Trend card's Period row is currently showing a custom (navigated-away) period. */
  engagementIsCustom(): boolean {
    return this.isPeriodCustom(this.engagementFilter, this.engagementWeekOffset, this.engagementDayOffset);
  }

  /** Label shown on the dropdown trigger for the currently selected filter (see getOnboardingFilterLabel). */
  getEngagementFilterLabel(): string {
    if (this.engagementIsCustom()) { return 'Custom'; }
    const match = this.engagementFilterOptions.find(opt => opt.value === this.engagementFilter);
    return match ? match.label : '';
  }

  /** Prev/Next arrows for the Engagement Trend card's Period row (see onOnboardingWeekNav). */
  onEngagementWeekNav(direction: 'prev' | 'next'): void {
    if (this.engagementFilter === 'last7') {
      if (direction === 'prev') {
        this.engagementWeekOffset += 1;
      } else {
        if (this.engagementWeekOffset === 0) { return; }
        this.engagementWeekOffset -= 1;
      }
      this.loadEngagementData(this.engagementFilter, this.engagementWeekOffset, this.engagementDayOffset);
      return;
    }

    if (this.engagementFilter === 'today' || this.engagementFilter === 'yesterday') {
      if (direction === 'prev') {
        this.engagementDayOffset += 1;
      } else {
        if (this.engagementDayOffset === 0) { return; }
        this.engagementDayOffset -= 1;
      }
      this.loadEngagementData(this.engagementFilter, this.engagementWeekOffset, this.engagementDayOffset);
      return;
    }
    // 'all' — no navigation.
  }

  /** Whether the ▶ (next) arrow should be disabled for the Engagement Trend card's Period row. */
  isEngagementNextDisabled(): boolean {
    if (this.engagementFilter === 'last7') { return this.engagementWeekOffset === 0; }
    if (this.engagementFilter === 'today' || this.engagementFilter === 'yesterday') { return this.engagementDayOffset === 0; }
    return true;
  }

  retryEngagementLoad(): void {
    this.loadEngagementData(this.engagementFilter, this.engagementWeekOffset, this.engagementDayOffset);
  }

  loadEngagementData(filter: string, weekOffset: number = 0, dayOffset: number = 0): void {
    this.isLoadingEngagement = true;
    this.engagementError = false;
    this.cdr.detectChanges();

    // /analytics/trend requires full ISO 8601 UTC datetimes. Reuses the same
    // Today/Yesterday/Last-7-Days UTC calculation as the Onboarding Trend
    // card (getOnboardingDateRangeParams) so all dropdown-driven cards send
    // identically-computed timestamps.
    const { start_date, end_date } = this.getOnboardingDateRangeParams(filter, weekOffset, dayOffset);
    this.engagementCurrentRange = { start_date, end_date };
    this.engagementDateRange = this.computePeriodRangeLabel(filter, weekOffset, dayOffset);
    // "All Time" omits start_date/end_date entirely rather than sending them empty.
    const engagementDateParams = (start_date && end_date)
      ? `?start_date=${encodeURIComponent(start_date)}&end_date=${encodeURIComponent(end_date)}`
      : '';

    (this.httpService.sendReGetRequest(
      `${URLUtils.getEngagementTrend}${engagementDateParams}`
    ) as unknown as Observable<EngagementTrendApiResponse>).subscribe(
      (res: EngagementTrendApiResponse) => {
        if (res && res.summary && res.graph) {
          const { summary, graph } = res;

          this.engagementTotal = summary?.total_count || 0;
          this.engagementPreviousTotal = summary?.previous_total || 0;
          this.engagementTrend = summary?.trend === 'up'
            ? Math.abs(summary.change_percentage)
            : summary?.trend === 'down'
              ? -Math.abs(summary.change_percentage)
              : 0;
          this.engagementVsLabel = filter === 'today' ? 'vs Yesterday'
            : filter === 'yesterday' ? 'vs Previous Day'
            : 'vs Previous 7 Days';

          this.engagementDatasets = graph?.datasets ?? [];

          const labels = graph?.x_axis?.labels ?? [];
          const under30Data: number[] = [];
          const midRangeData: number[] = [];
          const over90Data: number[] = [];
          const totalData: number[] = [];

          const findSegmentCount = (
            segments: { label: string; count: number }[] | undefined,
            key: string
          ): number => (segments ?? []).find(s => s.label === key)?.count || 0;

          labels.forEach(lbl => {
            const ds = this.engagementDatasets.find(d => d.label === lbl);
            const segments = ds?.details?.segments;
            under30Data.push(findSegmentCount(segments, '<30'));
            midRangeData.push(findSegmentCount(segments, '30-90'));
            over90Data.push(findSegmentCount(segments, '>90'));
            totalData.push(ds?.value ?? 0);
          });

          this.isLoadingEngagement = false;
          this.cdr.detectChanges();

          if (labels.length > 0) {
            setTimeout(() => this.buildEngagementChart(
              labels, under30Data, midRangeData, over90Data, totalData,
              graph.y_axis?.min, graph.y_axis?.max, graph.y_axis?.step, graph.y_axis?.label
            ), 0);
          }
        } else {
          this.isLoadingEngagement = false;
          this.engagementDatasets = [];
          this.cdr.detectChanges();
        }
      },
      (_err: any) => {
        console.error('[Dashboard] Engagement trend API error:', _err);
        this.isLoadingEngagement = false;
        this.engagementError = true;
        this.engagementDatasets = [];
        this.cdr.detectChanges();
      }
    );
  }

  buildEngagementChart(
    labels: string[],
    under30Data: number[],
    midRangeData: number[],
    over90Data: number[],
    totalData: number[],
    yMin?: number,
    yMax?: number,
    yStep?: number,
    yLabel?: string
  ): void {
    if (!this.engagementCanvas) { return; }
    this.destroyChart(this.engagementChartInstance);

    const ctx = this.engagementCanvas.nativeElement.getContext('2d');
    if (!ctx) { return; }

    // Build label→meta map so the tooltip can show total + per-bucket breakdown
    const metaMap = new Map<string, { total: number; under30: number; midRange: number; over90: number }>(
      labels.map((lbl, i) => [lbl, {
        total: totalData[i] ?? ((under30Data[i] || 0) + (midRangeData[i] || 0) + (over90Data[i] || 0)),
        under30: under30Data[i] || 0,
        midRange: midRangeData[i] || 0,
        over90: over90Data[i] || 0,
      }])
    );

    this.engagementChartInstance = new Chart(ctx, {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: '<30 mins',
            data: under30Data,
            borderColor: '#0788f2',
            backgroundColor: 'rgba(7,136,242,0.07)',
            fill: false,
            tension: 0.4,
            pointRadius: 3,
            pointHoverRadius: 6,
            pointHitRadius: 10,
            pointBackgroundColor: '#fff',
            pointBorderColor: '#0788f2',
            pointBorderWidth: 2,
            borderWidth: 2,
          },
          {
            label: '30–90 mins',
            data: midRangeData,
            borderColor: '#f97316',
            backgroundColor: 'rgba(249,115,22,0.07)',
            fill: false,
            tension: 0.4,
            pointRadius: 3,
            pointHoverRadius: 6,
            pointHitRadius: 10,
            pointBackgroundColor: '#fff',
            pointBorderColor: '#f97316',
            pointBorderWidth: 2,
            borderWidth: 2,
          },
          {
            label: '>90 mins',
            data: over90Data,
            borderColor: '#10b981',
            backgroundColor: 'rgba(16,185,129,0.07)',
            fill: false,
            tension: 0.4,
            pointRadius: 3,
            pointHoverRadius: 6,
            pointHitRadius: 10,
            pointBackgroundColor: '#fff',
            pointBorderColor: '#10b981',
            pointBorderWidth: 2,
            borderWidth: 2,
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 700, easing: 'easeOutQuart' },
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: '#fff',
            borderColor: '#e0e6ed',
            borderWidth: 1,
            titleColor: '#1a1a2e',
            titleFont: { size: 12.5, weight: 'bold' },
            bodyColor: '#374151',
            bodyFont: { size: 12 },
            padding: 12,
            cornerRadius: 10,
            boxPadding: 4,
            usePointStyle: true,
            callbacks: {
              title: (items: any[]) => items.length ? items[0].label : '',
              label: (ctx: any) => `  ${ctx.dataset.label}: ${ctx.parsed.y} Users`,
              afterBody: (items: any[]) => {
                if (!items.length) { return []; }
                const meta = metaMap.get(items[0].label);
                return meta ? ['', `Total Users: ${meta.total}`] : [];
              }
            }
          }
        },
        onClick: (evt: any, _elements: any[], chart: any) => {
          // Use 'nearest' with intersect:false and axis:'xy' (independent of the
          // tooltip's 'index' mode) so the click always resolves to *some* point —
          // whichever series/point is geometrically closest to the cursor in both
          // x and y — instead of requiring a pixel-perfect hit on the exact point
          // (intersect:true). intersect:true was why clicks would sometimes do
          // nothing even when the point had data: any click that wasn't within the
          // tiny point hit-radius returned zero elements. 'nearest' + axis:'xy'
          // still correctly tells the 3 overlapping series apart (it always picks
          // whichever one is actually closest to the cursor), but fires reliably
          // on every click, matching the onboarding chart's click reliability.
          const points = chart.getElementsAtEventForMode(evt, 'nearest', { intersect: false, axis: 'xy' }, true);
          if (points.length > 0) {
            const idx    = points[0].index;
            const dsIdx  = points[0].datasetIndex;
            const label  = labels[idx];
            const segmentKey: '<30' | '30-90' | '>90' =
              dsIdx === 0 ? '<30' : dsIdx === 1 ? '30-90' : '>90';
            this.navigateToEngagementUsers(label, segmentKey);
          }
        },
        onHover: (_evt: any, elements: any[]) => {
          const canvas = this.engagementCanvas?.nativeElement;
          if (canvas) { canvas.style.cursor = elements.length > 0 ? 'pointer' : 'default'; }
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { font: { size: 11 }, color: '#9ca3af', maxRotation: 0 }
          },
          y: {
            beginAtZero: yMin === undefined,
            ...(yMin !== undefined ? { min: yMin } : {}),
            ...(yMax !== undefined ? { max: yMax } : {}),
            title: { display: true, text: yLabel || 'Users', font: { size: 10.5 }, color: '#9ca3af' },
            grid: { color: 'rgba(0,0,0,0.04)' },
            ticks: {
              font: { size: 11 },
              color: '#9ca3af',
              stepSize: yStep ?? 1,
            },
            border: { display: false }
          }
        }
      }
    });
  }

  /**
   * Drill-down navigation for the Engagement Trend chart — clicking a point on
   * any of the 3 series (<30 / 30-90 / >90 mins) filters the lawyers list down
   * to the exact users behind that segment on that date/bucket.
   */
  navigateToEngagementUsers(pointLabel: string, segmentKey: '<30' | '30-90' | '>90'): void {
    const dataset = this.engagementDatasets.find(d => d.label === pointLabel);
    const segment = dataset?.details?.segments?.find(s => s.label === segmentKey);
    const count = segment?.count ?? 0;
    if (count === 0) { return; }

    const ids = (segment?.user_ids ?? []).filter(Boolean);
    if (!ids.length) { return; }

    const segmentLabel = segmentKey === '<30' ? '<30 mins'
                        : segmentKey === '30-90' ? '30–90 mins'
                        : '>90 mins';

    const dateRangeLabel = this.engagementFilter === 'today' ? 'Today'
                          : this.engagementFilter === 'yesterday' ? 'Yesterday'
                          : 'Last 7 Days';

    // NOTE: startDatetime/endDatetime are intentionally NOT forwarded here.
    // The engagement/trend response already gives us the exact user_ids behind
    // each segment, so the lawyers-list screen only needs to match on those IDs.
    // Previously we also forwarded engagementCurrentRange's start_date/end_date,
    // which lawyers-list used to additionally filter its server-side
    // getLauditorProfessionals call. That server-side date filter uses different
    // semantics than the engagement "time spent" window, so it could exclude
    // professionals from allProfessionals before the user_id match ever ran —
    // silently dropping legitimately-matched users from the drill-down. Passing
    // only engagementIds avoids that mismatch entirely.
    this.router.navigate(['/admin/lexiz-lawyers'], {
      queryParams: {
        engagementIds:     ids.join(','),
        engagementFilter:  this.engagementFilter,
        engagementPoint:   pointLabel,
        engagementSegment: segmentLabel,
        dateRange:         dateRangeLabel,
        account_type:      'client'
      }
    });
  }

  // ══════════════════════════════════════════════════════════
  //  Mobile App Usage Card — UPDATED for new API structure
  // ══════════════════════════════════════════════════════════

  onMobileFilterChange(filter: string): void {
    this.mobileFilter = filter;
    this.mobileWeekOffset = 0;
    this.mobileDayOffset = 0;
    this.loadMobileAppData(filter, this.mobileWeekOffset, this.mobileDayOffset);
  }

  /** Toggles the Mobile App Downloads card's All Time/Today/Yesterday/Last 7 Days dropdown. */
  toggleMobileFilterMenu(): void {
    this.mobileFilterMenuOpen = !this.mobileFilterMenuOpen;
  }

  /** Applies the chosen filter option and closes the dropdown (see selectOnboardingFilter for the "custom" no-op). */
  selectMobileFilter(filter: string): void {
    this.mobileFilterMenuOpen = false;
    if (filter === 'custom') { return; }
    this.onMobileFilterChange(filter);
  }

  /** Whether the Mobile App Downloads card's Period row is currently showing a custom (navigated-away) period. */
  mobileIsCustom(): boolean {
    return this.isPeriodCustom(this.mobileFilter, this.mobileWeekOffset, this.mobileDayOffset);
  }

  /** Label shown on the dropdown trigger for the currently selected filter (see getOnboardingFilterLabel). */
  getMobileFilterLabel(): string {
    if (this.mobileIsCustom()) { return 'Custom'; }
    const match = this.mobileFilterOptions.find(opt => opt.value === this.mobileFilter);
    return match ? match.label : '';
  }

  /** Prev/Next arrows for the Mobile App Downloads card's Period row (see onOnboardingWeekNav). */
  onMobileWeekNav(direction: 'prev' | 'next'): void {
    if (this.mobileFilter === 'last7') {
      if (direction === 'prev') {
        this.mobileWeekOffset += 1;
      } else {
        if (this.mobileWeekOffset === 0) { return; }
        this.mobileWeekOffset -= 1;
      }
      this.loadMobileAppData(this.mobileFilter, this.mobileWeekOffset, this.mobileDayOffset);
      return;
    }

    if (this.mobileFilter === 'today' || this.mobileFilter === 'yesterday') {
      if (direction === 'prev') {
        this.mobileDayOffset += 1;
      } else {
        if (this.mobileDayOffset === 0) { return; }
        this.mobileDayOffset -= 1;
      }
      this.loadMobileAppData(this.mobileFilter, this.mobileWeekOffset, this.mobileDayOffset);
      return;
    }
    // 'all' — no navigation.
  }

  /** Whether the ▶ (next) arrow should be disabled for the Mobile App Downloads card's Period row. */
  isMobileNextDisabled(): boolean {
    if (this.mobileFilter === 'last7') { return this.mobileWeekOffset === 0; }
    if (this.mobileFilter === 'today' || this.mobileFilter === 'yesterday') { return this.mobileDayOffset === 0; }
    return true;
  }

  loadMobileAppData(filter: string, weekOffset: number = 0, dayOffset: number = 0): void {
    this.isLoadingMobile = true;
    this.cdr.detectChanges();

    // Reuses the Onboarding Trend card's Today/Yesterday/Last-7-Days UTC
    // calculation (getOnboardingDateRangeParams) so all dropdown-driven
    // cards send identically-computed timestamps.
    const { start_date, end_date } = this.getOnboardingDateRangeParams(filter, weekOffset, dayOffset);
    this.mobileCurrentRange = { start_date, end_date };
    this.mobileDateRange = this.computePeriodRangeLabel(filter, weekOffset, dayOffset);
    // "All Time" omits start_date/end_date entirely rather than sending them empty.
    const mobileDateParams = (start_date && end_date)
      ? `?start_date=${encodeURIComponent(start_date)}&end_date=${encodeURIComponent(end_date)}`
      : '';

    (this.httpService.sendReGetRequest(
      `${URLUtils.getDownloadTrend}${mobileDateParams}`
    ) as unknown as Observable<DownloadTrendApiResponse>).subscribe(
      (res: DownloadTrendApiResponse) => {
        if (res && res.summary && res.graph) {
          const { summary, graph } = res;

          // Store datasets for drill-down navigation
          this.mobileApiDatasets = graph.datasets;

          // ── Extract Android and iOS totals from the summary structure ──
          const androidSummary = summary.android;
          const iosSummary = summary.ios;

          this.mobileAndroidTotal = androidSummary?.total_count || 0;
          this.mobileIosTotal = iosSummary?.total_count || 0;

          // ── Extract trends per platform ──
          this.mobileAndroidTrend = androidSummary?.trend === 'up'
            ? Math.abs(androidSummary.change_percentage)
            : androidSummary?.trend === 'down'
              ? -Math.abs(androidSummary.change_percentage)
              : 0;

          this.mobileIosTrend = iosSummary?.trend === 'up'
            ? Math.abs(iosSummary.change_percentage)
            : iosSummary?.trend === 'down'
              ? -Math.abs(iosSummary.change_percentage)
              : 0;

          // ── Build per-label android/ios counts from the new details.platforms[] structure ──
          const labels = graph.x_axis.labels;
          const androidData: number[] = [];
          const iosData: number[] = [];

          labels.forEach(lbl => {
            const ds = graph.datasets.find(d => d.label === lbl);
            const platforms = ds?.details?.platforms ?? [];
            androidData.push(platforms.find(p => p.platform === 'android')?.count || 0);
            iosData.push(platforms.find(p => p.platform === 'ios')?.count || 0);
          });

          this.isLoadingMobile = false;
          this.cdr.detectChanges();
          setTimeout(() => this.buildMobileChart(
            labels, androidData, iosData,
            graph.y_axis.max, graph.y_axis.step
          ), 0);
        } else {
          this.isLoadingMobile = false;
          this.cdr.detectChanges();
        }
      },
      (_err: any) => {
        console.error('[Dashboard] Mobile app usage API error:', _err);
        this.isLoadingMobile = false;
        this.cdr.detectChanges();
      }
    );
  }

  /** Flatten all devices from all datasets of a mobile trend response (new details.platforms[] structure) */
  private flattenMobileDevices(
    datasets: DownloadTrendApiResponse['graph']['datasets']
  ): DownloadTrendDevice[] {
    return datasets.flatMap(ds => this.flattenDatasetDevices(ds));
  }

  /** Flatten devices from a single dataset (new details.platforms[].devices structure) */
  private flattenDatasetDevices(
    ds: DownloadTrendApiResponse['graph']['datasets'][0]
  ): DownloadTrendDevice[] {
    return (ds.details?.platforms ?? []).flatMap(p => p.devices ?? []);
  }

  /** Devices for a single platform within one dataset bucket (new details.platforms[] structure) */
  private getPlatformDevices(
    ds: DownloadTrendApiResponse['graph']['datasets'][0] | undefined,
    platform: 'android' | 'ios'
  ): DownloadTrendDevice[] {
    return (ds?.details?.platforms ?? []).find(p => p.platform === platform)?.devices ?? [];
  }

  buildMobileChart(labels: string[], androidData: number[], iosData: number[], yMax?: number, yStep?: number): void {
    if (!this.mobileCanvas) { return; }
    this.destroyChart(this.mobileChartInstance);

    const ctx = this.mobileCanvas.nativeElement.getContext('2d');
    if (!ctx) { return; }

    this.mobileChartInstance = new Chart(ctx, {
      type: 'bar',
      data: {
        labels,
        datasets: [
          {
            label: 'Android',
            data: androidData,
            backgroundColor: 'rgba(7,136,242,0.82)',
            hoverBackgroundColor: '#0788f2',
            borderRadius: 5,
            borderSkipped: false,
            barPercentage: 0.55,
            categoryPercentage: 0.7,
          },
          {
            label: 'iOS',
            data: iosData,
            backgroundColor: 'rgba(139,92,246,0.75)',
            hoverBackgroundColor: '#8b5cf6',
            borderRadius: 5,
            borderSkipped: false,
            barPercentage: 0.55,
            categoryPercentage: 0.7,
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: '#fff',
            borderColor: '#e0e6ed',
            borderWidth: 1,
            titleColor: '#374151',
            bodyColor: '#374151',
            padding: 10,
            callbacks: {
              title: (items: any[]) => items.length ? items[0].label : '',
              label: (ctx: any) => `  ${ctx.dataset.label}: ${ctx.parsed.y.toLocaleString()}`
            }
          }
        },
        onClick: (_evt: any, elements: any[]) => {
          if (elements.length > 0) {
            const barIndex  = elements[0].index;
            const platform  = elements[0].datasetIndex === 0 ? 'android' : 'ios';
            const barLabel  = labels[barIndex];
            this.navigateToMobileBarUsers(barLabel, platform);
          }
        },
        onHover: (_evt: any, elements: any[]) => {
          const canvas = this.mobileCanvas?.nativeElement;
          if (canvas) { canvas.style.cursor = elements.length > 0 ? 'pointer' : 'default'; }
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { font: { size: 11 }, color: '#9ca3af', maxRotation: 0 }
          },
          y: {
            beginAtZero: true,
            ...(yMax !== undefined ? { max: yMax } : {}),
            grid: { color: 'rgba(0,0,0,0.04)' },
            ticks: {
              font: { size: 11 },
              color: '#9ca3af',
              stepSize: yStep ?? 1,
              maxTicksLimit: 8
            },
            border: { display: false }
          }
        }
      }
    });
  }

  /**
   * Navigate to lawyers list filtered by the devices from a specific bar click.
   * Extracts all user_ids from the clicked bar's dataset entry.
   */
  navigateToMobileBarUsers(barLabel: string, platform: 'android' | 'ios'): void {
    // Find the dataset entry for this specific bar
    const ds = this.mobileApiDatasets.find(d => d.label === barLabel);

    // Extract platform-specific devices from the bar's details (new details.platforms[] structure)
    const barDevices: DownloadTrendDevice[] = this.getPlatformDevices(ds, platform);

    // Deduplicate valid user_ids (null entries are anonymous installs, excluded)
    const userIds = [...new Set(
      barDevices.map(d => d.user_id).filter((id): id is string => !!id)
    )];

    const dateRangeLabel = this.mobileFilter === 'today' ? 'Today'
                         : this.mobileFilter === 'yesterday' ? 'Yesterday'
                         : 'Last 7 Days';

    const { start_date, end_date } = this.mobileCurrentRange;

    const queryParams: any = {
      mobileFilter:   this.mobileFilter,
      mobilePlatform: platform,
      mobileBarLabel: barLabel,
      dateRange:      dateRangeLabel,
      account_type:   'client',
      ...(start_date && end_date ? { startDatetime: start_date, endDatetime: end_date } : {})
    };

    if (userIds.length > 0) {
      queryParams['mobileUserIds'] = userIds.join(',');
    }

    this.router.navigate(['/admin/lexiz-lawyers'], { queryParams });
  }

  navigateToMobileUsers(platform: 'android' | 'ios'): void {
    // KPI metric click → show ALL users for the selected platform across the entire period.
    const platformDevices: DownloadTrendDevice[] = this.mobileApiDatasets.flatMap(ds =>
      this.getPlatformDevices(ds, platform)
    );

    // Deduplicate valid user_ids
    const userIds = [...new Set(
      platformDevices.map(d => d.user_id).filter((id): id is string => !!id)
    )];

    const dateRangeLabel = this.mobileFilter === 'today' ? 'Today'
                         : this.mobileFilter === 'yesterday' ? 'Yesterday'
                         : 'Last 7 Days';

    const { start_date, end_date } = this.mobileCurrentRange;

    const queryParams: any = {
      mobileFilter:   this.mobileFilter,
      mobilePlatform: platform,
      dateRange:      dateRangeLabel,
      account_type:   'client',
      ...(start_date && end_date ? { startDatetime: start_date, endDatetime: end_date } : {})
    };

    if (userIds.length > 0) {
      queryParams['mobileUserIds'] = userIds.join(',');
    }

    this.router.navigate(['/admin/lexiz-lawyers'], { queryParams });
  }

  // ══════════════════════════════════════════════════════════
  //  Lawyer Distribution by Location Card
  // ══════════════════════════════════════════════════════════

  onLocationFilterChange(filter: string): void {
    this.locationFilter = filter;
    this.locationWeekOffset = 0;
    this.locationDayOffset = 0;
    this.loadLocationDistribution(filter, this.locationWeekOffset, this.locationDayOffset);
  }

  /** Toggles the Lawyer Distribution card's All Time/Today/Yesterday/Last 7 Days dropdown. */
  toggleLocationFilterMenu(): void {
    this.locationFilterMenuOpen = !this.locationFilterMenuOpen;
  }

  /** Applies the chosen filter option and closes the dropdown (see selectOnboardingFilter for the "custom" no-op). */
  selectLocationFilter(filter: string): void {
    this.locationFilterMenuOpen = false;
    if (filter === 'custom') { return; }
    this.onLocationFilterChange(filter);
  }

  /** Whether the Lawyer Distribution card's Period row is currently showing a custom (navigated-away) period. */
  locationIsCustom(): boolean {
    return this.isPeriodCustom(this.locationFilter, this.locationWeekOffset, this.locationDayOffset);
  }

  /** Label shown on the dropdown trigger for the currently selected filter (see getOnboardingFilterLabel). */
  getLocationFilterLabel(): string {
    if (this.locationIsCustom()) { return 'Custom'; }
    const match = this.locationPracticeFilterOptions.find(opt => opt.value === this.locationFilter);
    return match ? match.label : '';
  }

  /** Prev/Next arrows for the Lawyer Distribution card's Period row (see onOnboardingWeekNav). */
  onLocationWeekNav(direction: 'prev' | 'next'): void {
    if (this.locationFilter === 'last7') {
      if (direction === 'prev') {
        this.locationWeekOffset += 1;
      } else {
        if (this.locationWeekOffset === 0) { return; }
        this.locationWeekOffset -= 1;
      }
      this.loadLocationDistribution(this.locationFilter, this.locationWeekOffset, this.locationDayOffset);
      return;
    }

    if (this.locationFilter === 'today' || this.locationFilter === 'yesterday') {
      if (direction === 'prev') {
        this.locationDayOffset += 1;
      } else {
        if (this.locationDayOffset === 0) { return; }
        this.locationDayOffset -= 1;
      }
      this.loadLocationDistribution(this.locationFilter, this.locationWeekOffset, this.locationDayOffset);
      return;
    }
    // 'all' — no navigation.
  }

  /** Whether the ▶ (next) arrow should be disabled for the Lawyer Distribution card's Period row. */
  isLocationNextDisabled(): boolean {
    if (this.locationFilter === 'last7') { return this.locationWeekOffset === 0; }
    if (this.locationFilter === 'today' || this.locationFilter === 'yesterday') { return this.locationDayOffset === 0; }
    return true;
  }

  loadLocationDistribution(filter: string, weekOffset: number = 0, dayOffset: number = 0): void {
    this.isLoadingLocation = true;
    this.cdr.detectChanges();

    // Reuses the Onboarding Trend card's Today/Yesterday/Last-7-Days UTC
    // calculation (getOnboardingDateRangeParams) so all dropdown-driven
    // cards send identically-computed timestamps.
    const { start_date, end_date } = this.getOnboardingDateRangeParams(filter, weekOffset, dayOffset);
    this.locationDateRange = this.computePeriodRangeLabel(filter, weekOffset, dayOffset);
    const dateParams = (start_date && end_date)
      ? `?start_date=${encodeURIComponent(start_date)}&end_date=${encodeURIComponent(end_date)}`
      : '';

    (this.httpService.sendReGetRequest(
      `${URLUtils.getLocationDistribution}${dateParams}`
    ) as unknown as Observable<LocationDistributionApiResponse>).subscribe(
      (res: LocationDistributionApiResponse) => {
        if (res && !res.error && res.data) {
          const { summary, cities } = res.data;
          this.locationTotalCities  = summary.total_cities;
          this.locationTotalLawyers = summary.total_lawyers;
          const topCity = summary.top_cities;
          this.locationTopCity = topCity ? `${topCity.city} (${topCity.count})` : '';

          const sorted = [...cities].sort((a, b) => b.count - a.count);
          this.locationTopCities       = sorted;
          this.locationRemainingCities = 0;
          this.locationMaxCount        = sorted[0]?.count ?? 1;
        } else {
          this.locationTopCities = [];
          this.locationRemainingCities = 0;
        }
        this.isLoadingLocation = false;
        this.cdr.detectChanges();
      },
      (_err: any) => {
        this.isLoadingLocation = false;
        this.locationTopCities = [];
        this.cdr.detectChanges();
      }
    );
  }

  getLocationBarWidth(count: number): number {
    if (!this.locationMaxCount) { return 0; }
    return Math.round((count / this.locationMaxCount) * 100);
  }

  getLocationPct(count: number): string {
    if (!this.locationTotalLawyers) { return '0'; }
    return ((count / this.locationTotalLawyers) * 100).toFixed(1);
  }

  getPracticeItemPct(count: number): string {
    if (!this.practiceTotalLawyers) { return '0'; }
    return ((count / this.practiceTotalLawyers) * 100).toFixed(1);
  }

  navigateToLocationList(): void {
    const { start_date, end_date } = this.getOnboardingDateRangeParams(this.locationFilter, this.locationWeekOffset, this.locationDayOffset);
    this.router.navigate(['/admin/lexiz-lawyers'], {
      queryParams: {
        locationFilter: this.locationFilter,
        account_type:   'client',
        ...(start_date && end_date ? { startDatetime: start_date, endDatetime: end_date } : {})
      }
    });
  }

  navigateToLocationCity(city: LocationCityItem): void {
    const dateRangeLabel = this.locationFilter === 'all' ? 'All Time'
                         : this.locationFilter === 'today' ? 'Today'
                         : this.locationFilter === 'yesterday' ? 'Yesterday'
                         : 'Last 7 Days';
    const { start_date, end_date } = this.getOnboardingDateRangeParams(this.locationFilter, this.locationWeekOffset, this.locationDayOffset);
    this.router.navigate(['/admin/lexiz-lawyers'], {
      queryParams: {
        locationCity:   city.city,
        locationFilter: this.locationFilter,
        dateRange:      dateRangeLabel,
        account_type:   'client',
        ...(start_date && end_date ? { startDatetime: start_date, endDatetime: end_date } : {})
      }
    });
  }

  navigateToPracticeItem(item: PracticeDistributionItem): void {
    const dateRangeLabel = this.practiceFilter === 'all' ? 'All Time'
                         : this.practiceFilter === 'today' ? 'Today'
                         : this.practiceFilter === 'yesterday' ? 'Yesterday'
                         : 'Last 7 Days';
    const paramKey = this.practiceActiveTab === 'practice' ? 'practiceArea' : 'serviceFilter';
    const { start_date, end_date } = this.getOnboardingDateRangeParams(this.practiceFilter, this.practiceWeekOffset, this.practiceDayOffset);
    const queryParams: any = {
      [paramKey]:     item.name,
      practiceTab:    this.practiceActiveTab,
      practiceFilter: this.practiceFilter,
      dateRange:      dateRangeLabel,
      account_type:   'client',
      ...(start_date && end_date ? { startDatetime: start_date, endDatetime: end_date } : {})
    };
    if (this.practiceSelectedCity) {
      queryParams['locationCity'] = this.practiceSelectedCity;
    }
    this.router.navigate(['/admin/lexiz-lawyers'], { queryParams });
  }

  // ══════════════════════════════════════════════════════════
  //  Practice Areas & Services Distribution Card
  // ══════════════════════════════════════════════════════════

  onPracticeFilterChange(filter: string): void {
    this.practiceFilter = filter;
    this.practiceWeekOffset = 0;
    this.practiceDayOffset = 0;
    this.loadPracticeDistribution();
  }

  /** Toggles the Practice Areas & Services card's All Time/Today/Yesterday/Last 7 Days dropdown. */
  togglePracticeFilterMenu(): void {
    this.practiceFilterMenuOpen = !this.practiceFilterMenuOpen;
  }

  /** Applies the chosen filter option and closes the dropdown (see selectOnboardingFilter for the "custom" no-op). */
  selectPracticeFilter(filter: string): void {
    this.practiceFilterMenuOpen = false;
    if (filter === 'custom') { return; }
    this.onPracticeFilterChange(filter);
  }

  /** Whether the Practice Areas & Services card's Period row is currently showing a custom (navigated-away) period. */
  practiceIsCustom(): boolean {
    return this.isPeriodCustom(this.practiceFilter, this.practiceWeekOffset, this.practiceDayOffset);
  }

  /** Label shown on the dropdown trigger for the currently selected filter (see getOnboardingFilterLabel). */
  getPracticeFilterLabel(): string {
    if (this.practiceIsCustom()) { return 'Custom'; }
    const match = this.locationPracticeFilterOptions.find(opt => opt.value === this.practiceFilter);
    return match ? match.label : '';
  }

  /** Prev/Next arrows for the Practice Areas & Services card's Period row (see onOnboardingWeekNav). */
  onPracticeWeekNav(direction: 'prev' | 'next'): void {
    if (this.practiceFilter === 'last7') {
      if (direction === 'prev') {
        this.practiceWeekOffset += 1;
      } else {
        if (this.practiceWeekOffset === 0) { return; }
        this.practiceWeekOffset -= 1;
      }
      this.loadPracticeDistribution();
      return;
    }

    if (this.practiceFilter === 'today' || this.practiceFilter === 'yesterday') {
      if (direction === 'prev') {
        this.practiceDayOffset += 1;
      } else {
        if (this.practiceDayOffset === 0) { return; }
        this.practiceDayOffset -= 1;
      }
      this.loadPracticeDistribution();
      return;
    }
    // 'all' — no navigation.
  }

  /** Whether the ▶ (next) arrow should be disabled for the Practice Areas & Services card's Period row. */
  isPracticeNextDisabled(): boolean {
    if (this.practiceFilter === 'last7') { return this.practiceWeekOffset === 0; }
    if (this.practiceFilter === 'today' || this.practiceFilter === 'yesterday') { return this.practiceDayOffset === 0; }
    return true;
  }

  onPracticeTabChange(tab: 'practice' | 'services'): void {
    this.practiceActiveTab = tab;
    this.practiceSearchQuery = '';
    this.applyPracticeSearch();
  }

  onPracticeCityChange(city: string): void {
    this.practiceSelectedCity = city;
    this.loadPracticeDistribution();
  }

  onPracticeSearchChange(_query: string): void {
    this.applyPracticeSearch();
  }

  clearPracticeSearch(): void {
    this.practiceSearchQuery = '';
    this.applyPracticeSearch();
  }

  private applyPracticeSearch(): void {
    const q = (this.practiceSearchQuery || '').toLowerCase().trim();
    const source = this.practiceActiveTab === 'practice'
      ? this.practiceAreasFull
      : this.servicesFull;

    this.practiceFilteredList = q
      ? source.filter(item => item.name.toLowerCase().includes(q))
      : [...source];

    this.practiceMaxCount = source.length > 0 ? Math.max(...source.map(item => item.count)) : 1;
  }

  getPracticeBarWidth(count: number): number {
    if (!this.practiceMaxCount) { return 0; }
    return Math.round((count / this.practiceMaxCount) * 100);
  }

  loadPracticeDistribution(): void {
    this.isLoadingPractice = true;
    this.cdr.detectChanges();

    // Reuses the Onboarding Trend card's Today/Yesterday/Last-7-Days UTC
    // calculation (getOnboardingDateRangeParams) so all dropdown-driven
    // cards send identically-computed timestamps.
    const { start_date, end_date } = this.getOnboardingDateRangeParams(this.practiceFilter, this.practiceWeekOffset, this.practiceDayOffset);
    this.practiceDateRange = this.computePeriodRangeLabel(this.practiceFilter, this.practiceWeekOffset, this.practiceDayOffset);
    let params = '';
    const parts: string[] = [];
    if (start_date && end_date) {
      parts.push(`start_date=${encodeURIComponent(start_date)}`, `end_date=${encodeURIComponent(end_date)}`);
    }
    if (this.practiceSelectedCity) {
      parts.push(`city=${encodeURIComponent(this.practiceSelectedCity)}`);
    }
    if (parts.length) { params = '?' + parts.join('&'); }

    (this.httpService.sendReGetRequest(
      `${URLUtils.getPracticeDistribution}${params}`
    ) as unknown as Observable<PracticeDistributionApiResponse>).subscribe(
      (res: PracticeDistributionApiResponse) => {
        if (res && !res.error && res.data) {
          const { summary, practice_areas, services_offered } = res.data;

          this.practiceTotalLawyers = summary.total_lawyers;

          this.practiceAreasFull  = practice_areas ?? [];
          this.practiceTotalAreas = this.practiceAreasFull.length;
          const topArea = summary.top_practice_area;
          this.practiceTopAreaName  = topArea?.name ?? '';
          this.practiceTopAreaCount = topArea?.count ?? 0;
          this.practiceTopAreaPct   = this.practiceTotalLawyers > 0 && topArea
            ? ((topArea.count / this.practiceTotalLawyers) * 100).toFixed(1)
            : '0';

          this.servicesFull          = services_offered ?? [];
          this.practiceTotalServices = this.servicesFull.length;
          const topSvc = summary.top_service;
          this.practiceTopServiceName  = topSvc?.name ?? '';
          this.practiceTopServiceCount = topSvc?.count ?? 0;
          this.practiceTopServicePct   = this.practiceTotalLawyers > 0 && topSvc
            ? ((topSvc.count / this.practiceTotalLawyers) * 100).toFixed(1)
            : '0';

          this.applyPracticeSearch();
        } else {
          this.practiceFilteredList = [];
        }
        this.isLoadingPractice = false;
        this.cdr.detectChanges();
      },
      (_err: any) => {
        this.isLoadingPractice = false;
        this.practiceFilteredList = [];
        this.cdr.detectChanges();
      }
    );
  }

  loadCityList(): void {
    (this.httpService.sendReGetRequest(
      URLUtils.getCityList
    ) as unknown as Observable<CityListApiResponse>).subscribe(
      (res: CityListApiResponse) => {
        if (res && !res.error && Array.isArray(res.data) && res.data.length > 0) {
          this.practiceCityOptions = res.data.map(d => d.city);
        }
        this.loadPracticeDistribution();
      },
      (_err: any) => {
        this.loadPracticeDistribution();
      }
    );
  }

  /**
   * /location-distribution and /practice-distribution require ISO 8601 UTC
   * datetimes (e.g. 2026-07-05T18:30Z) for last24h/last7, same format as the
   * Engagement Trend / Login / Mobile / Onboarding cards — not date-only.
   */
  private getDateRangeParams(filter: string): { start_date: string; end_date: string } {
    const toIsoUtc = (d: Date): string => d.toISOString().replace(/:\d{2}\.\d{3}Z$/, 'Z');

    const now = new Date();

    if (filter === 'last24h') {
      // Rolling 24-hour window: exactly 24h ago → now (e.g. yesterday 12:30pm → today 12:30pm)
      const start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      return { start_date: toIsoUtc(start), end_date: toIsoUtc(now) };
    }
    if (filter === 'last7') {
      // Rolling 7-day window: exactly 7 days ago at same time → now
      const start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      return { start_date: toIsoUtc(start), end_date: toIsoUtc(now) };
    }
    if (filter === 'last30') {
      // Rolling 30-day window: exactly 30 days ago at same time → now
      const start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      return { start_date: toIsoUtc(start), end_date: toIsoUtc(now) };
    }
    return { start_date: '', end_date: '' };
  }

  /**
   * /login/trend now requires ISO 8601 UTC datetimes (e.g. 2026-07-05T18:30Z)
   * instead of being called with no params. The "Total Logged-in Users (24h)"
   * card has no filter buttons of its own — it's always the last-24h window.
   */
  private getLoginDateRangeParams(): { start_date: string; end_date: string } {
    const toIsoUtc = (d: Date): string => d.toISOString().replace(/:\d{2}\.\d{3}Z$/, 'Z');
    const now = new Date();
    const start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    return { start_date: toIsoUtc(start), end_date: toIsoUtc(now) };
  }

  /**
   * /download/trend now requires ISO 8601 UTC datetimes (e.g. 2026-07-05T18:30Z)
   * instead of the old `?filter=daily|weekly|monthly` param. The existing
   * last24h/last7/last30 filter buttons are mapped to rolling windows ending now.
   */
  private getMobileDateRangeParams(filter: string): { start_date: string; end_date: string } {
    const toIsoUtc = (d: Date): string => d.toISOString().replace(/:\d{2}\.\d{3}Z$/, 'Z');

    const now = new Date();
    let start: Date;

    if (filter === 'last7') {
      start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    } else if (filter === 'last30') {
      start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    } else {
      // last24h (default)
      start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    }

    return { start_date: toIsoUtc(start), end_date: toIsoUtc(now) };
  }

  // ── Reusable local-time / UTC-ISO utility functions ────────
  // Used by getOnboardingDateRangeParams (and available for future filters,
  // e.g. "Last 30 Days" or a custom range) so date-calculation logic isn't
  // duplicated per filter branch.

  /** Adds (or subtracts, for negative n) whole calendar days to a Date, in local time. */
  private addDays(d: Date, n: number): Date {
    const result = new Date(d);
    result.setDate(result.getDate() + n);
    return result;
  }

  /** Midnight (00:00:00.000) of the given date, in the client's local timezone. */
  private startOfLocalDay(d: Date): Date {
    const result = new Date(d);
    result.setHours(0, 0, 0, 0);
    return result;
  }

  /** 23:59:59.999 of the given date, in the client's local timezone. */
  private endOfLocalDay(d: Date): Date {
    const result = new Date(d);
    result.setHours(23, 59, 59, 999);
    return result;
  }

  /** Formats a Date as UTC ISO-8601 seconds precision: YYYY-MM-DDTHH:mm:ssZ. */
  private toIsoUtcSeconds(d: Date): string {
    return d.toISOString().replace(/\.\d{3}Z$/, 'Z');
  }

  /**
   * Local calendar start/end days (not UTC instants) for the "Last 7 Days"
   * filter at a given week offset:
   *  - offset 0  → the current/live 7-day window: [today-6, today]
   *  - offset N  → the Nth fully-completed 7-day window before that,
   *                i.e. [today-7N, today-7N+6]
   * Shared by getOnboardingDateRangeParams (for the API payload) and
   * setOnboardingDateRange (for the "Jul 13 – Jul 19" display label) so the
   * two never drift apart.
   */
  private getOnboardingLast7LocalDays(weekOffset: number): { start: Date; end: Date } {
    const today = new Date();
    if (weekOffset <= 0) {
      return { start: this.addDays(today, -6), end: today };
    }
    const end = this.addDays(today, -7 * weekOffset + 6);
    const start = this.addDays(today, -7 * weekOffset);
    return { start, end };
  }

  /**
   * /onboard/trend requires ISO 8601 UTC datetimes (e.g. 2026-07-05T18:30:00Z).
   * The Onboarding Trend card's dropdown uses 'today' | 'yesterday' | 'last7'
   * (with weekOffset for Last-7-Days navigation, dayOffset for Today/Yesterday
   * navigation); 'daily'/'weekly'/'monthly'/'quarterly' are kept for the
   * "Newly Onboarded Users" summary card and the hidden Quarterly option,
   * respectively — unchanged from before.
   *
   * dayOffset counts calendar days back from the selected anchor:
   *  - filter 'today', dayOffset 0     → today (live, ends "now")
   *  - filter 'today', dayOffset N     → N days before today (a completed day)
   *  - filter 'yesterday', dayOffset 0 → yesterday (always a completed day)
   *  - filter 'yesterday', dayOffset N → N+1 days before today
   * This lets Today and Yesterday each be navigated independently via ◀/▶
   * without crossing into one another's range (see onOnboardingWeekNav and
   * its per-card counterparts).
   *
   * All calendar-day boundaries below are computed in the client's local
   * timezone, then converted to UTC for the API payload.
   */
  private getOnboardingDateRangeParams(filter: string, weekOffset: number = 0, dayOffset: number = 0): { start_date: string; end_date: string } {
    const now = new Date();

    // "All Time" — no date filter at all; callers already guard on
    // `if (start_date && end_date)` before adding these to the request, so
    // returning empty strings here correctly omits both start_date and
    // end_date from the payload.
    if (filter === 'all') {
      return { start_date: '', end_date: '' };
    }

    if (filter === 'today') {
      const target = this.addDays(now, -dayOffset);
      const start = this.startOfLocalDay(target);
      // Only the live/current day (offset 0) ends "now"; navigated-to past
      // days are completed days and end at 23:59:59 local.
      const end = dayOffset === 0 ? now : this.endOfLocalDay(target);
      return { start_date: this.toIsoUtcSeconds(start), end_date: this.toIsoUtcSeconds(end) };
    }

    if (filter === 'yesterday') {
      const y = this.addDays(now, -(1 + dayOffset));
      const start = this.startOfLocalDay(y);
      const end = this.endOfLocalDay(y);
      return { start_date: this.toIsoUtcSeconds(start), end_date: this.toIsoUtcSeconds(end) };
    }

    if (filter === 'last7') {
      const { start: startDay, end: endDay } = this.getOnboardingLast7LocalDays(weekOffset);
      const start = this.startOfLocalDay(startDay);
      // The live/current window (offset 0) ends "now"; fully-completed past
      // windows (offset >= 1) end at 23:59:59 local of their last day.
      const end = weekOffset <= 0 ? now : this.endOfLocalDay(endDay);
      return { start_date: this.toIsoUtcSeconds(start), end_date: this.toIsoUtcSeconds(end) };
    }

    // ── Legacy branches (kept, not removed) ──────────────────
    const toIsoUtc = (d: Date): string => d.toISOString().replace(/:\d{2}\.\d{3}Z$/, 'Z');
    let start: Date;

    if (filter === 'weekly') {
      start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    } else if (filter === 'monthly') {
      start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    } else if (filter === 'quarterly') {
      start = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
    } else {
      // daily (default) — still used by the "Newly Onboarded Users" summary card
      start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    }

    return { start_date: toIsoUtc(start), end_date: toIsoUtc(now) };
  }

/**
 * /analytics/trend requires ISO 8601 UTC datetimes without seconds
 * (e.g. 2026-07-05T18:30Z), unlike the date-only params used by the
 * Location/Practice cards above.
 * 
 * For 7d: exactly 7 days difference ending yesterday (e.g., 2026-07-08T18:30Z to 2026-07-15T18:30Z)
 * For 30d: exactly 30 days difference ending yesterday (e.g., 2026-06-15T18:30Z to 2026-07-15T18:30Z)
 * For last24h: rolling 24-hour window
 */
private getEngagementDateRangeParams(filter: string): { start_date: string; end_date: string } {
  const toIsoUtc = (d: Date): string => {
    // Format: YYYY-MM-DDTHH:MMZ (without seconds)
    const year = d.getUTCFullYear();
    const month = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    const hours = '18'; // Fixed at 18:30Z
    const minutes = '30';
    return `${year}-${month}-${day}T${hours}:${minutes}Z`;
  };

  const now = new Date();
  
  // For last24h: keep rolling window
  if (filter === 'last24h') {
    const endDate = new Date(now);
    endDate.setUTCHours(18, 30, 0, 0);
    const startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    startDate.setUTCHours(18, 30, 0, 0);
    return { 
      start_date: toIsoUtc(startDate), 
      end_date: toIsoUtc(endDate) 
    };
  }

  // For last7 and last30: end date should be YESTERDAY at 18:30Z
  const endDate = new Date(now);
  endDate.setUTCDate(endDate.getUTCDate() - 1); // Yesterday
  endDate.setUTCHours(18, 30, 0, 0);
  
  let startDate: Date;

  if (filter === 'last7') {
    // Exactly 7 days before the end date (yesterday - 7 days)
    startDate = new Date(endDate);
    startDate.setUTCDate(endDate.getUTCDate() - 7);
    startDate.setUTCHours(18, 30, 0, 0);
  } else if (filter === 'last30') {
    // Exactly 30 days before the end date (yesterday - 30 days)
    startDate = new Date(endDate);
    startDate.setUTCDate(endDate.getUTCDate() - 30);
    startDate.setUTCHours(18, 30, 0, 0);
  } else {
    // Fallback (should not happen for last24h as it's handled above)
    startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    startDate.setUTCHours(18, 30, 0, 0);
  }

  return { 
    start_date: toIsoUtc(startDate), 
    end_date: toIsoUtc(endDate) 
  };
}

  // ══════════════════════════════════════════════════════════
  //  Helpers
  // ══════════════════════════════════════════════════════════

  getTrendClass(trend: number): string {
    return trend >= 0 ? 'trend-up' : 'trend-down';
  }

  getTrendIcon(trend: number): string {
    return trend >= 0 ? 'bi-arrow-up-right' : 'bi-arrow-down-right';
  }

  formatTrend(trend: number): string {
    return (trend >= 0 ? '+' : '') + trend.toFixed(1) + '%';
  }

  navigateToDashboard(): void {
    this.router.navigate(['/dashboard']);
  }
}